/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { observer } from 'mobx-react';
import { useState } from 'react';
import { Save } from '@carbon/icons-react';
import { TextArea, TextInput, Modal, Stack, Button } from '@carbon/react';
import { spacing05 } from '@carbon/layout';

import { idpProjectStore, structuredExtractionStore } from 'stores';

/**
 * @typedef {Object} PublishTemplateProps
 * @property {() => void} handleNavigationToVersions
 */
export const PublishTemplate = observer(function PublishTemplate({ handleNavigationToVersions }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { documentForExtraction } = structuredExtractionStore;
  const { idpProject } = idpProjectStore;
  const [versionName, setVersionName] = useState('');
  const [versionDescription, setVersionDescription] = useState('');

  const isPublishDisabled = !versionName.trim().length || !versionDescription.trim().length;

  const closeModal = () => {
    setVersionName('');
    setVersionDescription('');
    setIsModalOpen(false);
  };

  const performRequest = async () => {
    if (isPublishDisabled) {
      return;
    }
    await structuredExtractionStore.publishTemplate(idpProject.id, versionName, versionDescription);
    closeModal();
  };

  return (
    <Stack gap={spacing05} orientation="horizontal">
      <Button kind="ghost" onClick={handleNavigationToVersions}>
        Versions
      </Button>
      <Button kind="primary" renderIcon={Save} onClick={() => setIsModalOpen(true)}>
        Publish Template
      </Button>
      {isModalOpen && (
        <Modal
          open={isModalOpen}
          modalHeading="Publish Extraction Template"
          primaryButtonText="Publish"
          primaryButtonDisabled={isPublishDisabled}
          secondaryButtonText="Cancel"
          onRequestSubmit={performRequest}
          onRequestClose={closeModal}
          onSecondarySubmit={closeModal}
          preventCloseOnClickOutside
        >
          <Stack gap={spacing05}>
            <span>{`Publish '${documentForExtraction.name}' to your project to allow members to use it in their diagrams.`}</span>
            <TextInput
              data-modal-primary-focus
              id="version-name"
              autoComplete="off"
              labelText="Version name"
              placeholder="1.0"
              value={versionName}
              maxCount={255}
              onChange={(event) => setVersionName(event.target.value)}
            />
            <TextArea
              enableCounter
              id="version-description"
              labelText="Version description"
              value={versionDescription}
              maxCount={255}
              onChange={(event) => setVersionDescription(event.target.value)}
            />
          </Stack>
        </Modal>
      )}
    </Stack>
  );
});
